import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { ProjectId, StatusUpdateId } from 'api/models';
import { StatusUpdate, StatusUpdateState } from 'api/status-updates';
import { Loading } from 'components/atoms/loading';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { resourceSectionRenderer } from 'components/molecules/resource-section-renderer';
import {
  CKCollabEditor,
  EditorsObject,
} from 'components/organisms/ck-collab-editor';
import {
  saveAllEditors,
  setActiveEditor,
  useDocuments,
} from 'components/organisms/document/document-store';
import { taskSectionRenderer } from 'components/organisms/document/parts/editor/task-section';
import styles from 'components/organisms/status-updates/editor/editor.module.css';
import { FetchStatus } from 'data/core';
import { EditorModes, getDocTasks, setActiveDocument } from 'data/documents';
import {
  selectActiveStatusUpdate,
  setActiveStatusUpdate,
} from 'data/status-updates';
import { saveTasks } from 'data/tasks/slice';
import { useIsResponsive } from 'hooks/use-media-query';
import { useGetStatusUpdateDetailsQuery } from 'queries/status-updates';
import { useProjectTasksQuery } from 'queries/task';
import { logger } from 'services';
import { queueMacrotask } from 'utils/macrotask';
import { TaskSection } from 'vendor/ckeditor/plugins/document-tasks-section/plugin';
import { ResourceSectionSchema } from 'vendor/ckeditor/plugins/resource-list-section';
import { ResourceListSection } from 'vendor/ckeditor/plugins/resource-list-section/plugin';

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RespStatusUpdateContent: React.FC = () => {
  const { projectId: _projectId, statusUpdateId: _statusUpdateId } =
    useParams<{ projectId: string; statusUpdateId: string }>();

  const projectId = Number(_projectId);
  const statusUpdateId = Number(_statusUpdateId);
  const ref = useRef<HTMLDivElement | null>(null);

  const { data } = useGetStatusUpdateDetailsQuery(
    StatusUpdateId(statusUpdateId),
  );
  const dispatch = useDispatch();
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  const respMode = useIsResponsive();

  useEffect(() => {
    if (data !== undefined && activeStatusUpdate === null) {
      dispatch(setActiveStatusUpdate(data));
    }
  }, [data, dispatch, activeStatusUpdate]);

  useProjectTasksQuery(ProjectId(projectId), {
    retry: 1,
    refetchOnMount: true,
    staleTime: 0,
    onSuccess: async (tasks) => {
      try {
        dispatch(saveTasks({ data: tasks, fetchStatus: FetchStatus.Loaded }));
      } catch (error) {
        logger.error('failed to fetch tasks', { error });
      }
    },
  });
  const [, documentStoreDispatch] = useDocuments();
  const [currentSU, changeSU] = useState<StatusUpdate | null>(null);
  const presenceListElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      activeStatusUpdate?.statusUpdateId !== currentSU?.statusUpdateId ||
      activeStatusUpdate?.state !== currentSU?.state
    ) {
      changeSU(null);
      // So that reloading the CK editor will work.
      queueMacrotask(() => {
        changeSU(activeStatusUpdate);
      });
    }
  }, [activeStatusUpdate, currentSU]);

  useEffect(() => {
    const docId = currentSU?.document?.documentId;

    if (docId) {
      dispatch(getDocTasks({ documentId: docId, projectId }));
      dispatch(
        setActiveDocument({
          documentId: docId,
          projectId,
        }),
      );
    }
  }, [currentSU?.document?.documentId, dispatch, projectId]);

  if (activeStatusUpdate === null) {
    return (
      <Wrapper>
        <Loading />
      </Wrapper>
    );
  }

  const onEditorReady = (allEditors: EditorsObject) => {
    if (allEditors[EditorModes.Public]) {
      setActiveEditor(documentStoreDispatch, allEditors[EditorModes.Public]);
    }
    saveAllEditors(documentStoreDispatch, allEditors);
  };

  const onEditorFocusChange = (editorT: Editor) => {
    setActiveEditor(documentStoreDispatch, editorT);
  };

  const resourceListConfig = {
    [ResourceSectionSchema]: {
      renderer: resourceSectionRenderer,
    },
  };

  const taskSectionConfig = {
    taskSection: {
      renderer: taskSectionRenderer,
    },
  };
  const fallBack = () => (
    <p>
      <FormattedMessage id="editor.error" />
    </p>
  );

  return (
    <>
      <div ref={ref} />
      <div
        className={`${styles.editor}  ${
          currentSU?.state === StatusUpdateState.Published || respMode
            ? styles.editor__readonly
            : ''
        }`}
      >
        <ErrorBoundary fallback={fallBack}>
          <div className={styles.presence} ref={presenceListElementRef} />

          {currentSU && presenceListElementRef.current ? (
            <CKCollabEditor
              className="status-update__editor"
              onEditorReady={onEditorReady}
              singleUserMode={currentSU?.state === StatusUpdateState.Published}
              readOnly={
                currentSU?.state === StatusUpdateState.Published || respMode
              }
              customClasses={{
                editorClass: styles.ck_editor,
              }}
              extraPlugins={[TaskSection, ResourceListSection]}
              extraConfig={{
                ...taskSectionConfig,
                ...resourceListConfig,
              }}
              presenceListElementRef={presenceListElementRef}
              docId={currentSU.document.documentId}
              projectId={projectId}
              onEditorFocusChange={onEditorFocusChange}
              shouldHidePlaceHolder={
                activeStatusUpdate?.state === StatusUpdateState.Published
              }
            />
          ) : null}
        </ErrorBoundary>
      </div>
    </>
  );
};
